<template>
    <div class="content-container">
        <div class="filter-container">
            <div>
                <h2>{{ $t('intervention.non-preventive.filter') }}</h2>
                <ButtonBlue
                    :disabled="tableData.length === 0 && isLoading"
                    :on-click="selectionnerTypeDocument"
                    :text="$t('bouton.selection.type.documents')"
                    class="button-confirm btn"
                />
            </div>
        </div>
        <div class="dataTable">
            <div class="container-header">
                <h2 tabindex="-1">
                    {{ $tc('document.reglementaire.dataTable.title', +count) }}
                </h2>
                <div class="button-container">
                    <BorderedIconButton
                        v-if="!isDownloadDisabled"
                        :imgUrl="iconeDownload"
                        :title="$t('list.download.selection')"
                        class="button-left"
                        @click="downloadDocuments"
                    />
                    <BorderedIconButton
                        v-if="count > 0"
                        :imgUrl="iconeDownload"
                        :title="$t('list.download.all')"
                        class="button-right"
                        @click="downloadAll"
                    />
                </div>
            </div>
            <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
            <div v-else ref="content" class="datatable-container-smaller" @scroll="handleScroll">
                <DataTable
                    :documentListToDownload="documentListToDownload"
                    :isLoading="isLoading"
                    :noDataText="$t('search.notFound')"
                    :tableData="tableData"
                    :tableHeader="tableHeader"
                    @select="doSelect"
                    @sort="doSort"
                />
            </div>
        </div>
    </div>
    <ModalChoixTypeDocuments
        v-if="displayModalTypeDocument && !isListTypesLoading"
        :selected-types="filter.documentTypeList"
        :types-documents="typeDocumentList"
        @cancel="modaleChoixTypesCancel"
        @valider="validerTypes"
    />
    <ModalDownloadDocuments
        v-if="showModal"
        :documentTypeList="typeDocumentList"
        :nb-documents-modal="nbDocumentsModal"
        @cancel="cancel"
        @downloadDocuments="setFilterForDownloadAllDocuments"
    />
    <ModalChoixNomZip v-if="showModalNomZip" @cancel="showModalNomZip = false" @download="downloadAllDocuments" />
    <WaitingModal v-if="isZipLoading || isListTypesLoading" />
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import {
    DocumentTypeSource,
    TypeDocumentReglementaire,
} from '@/models/TypeDocumentReglementaire';
import { DocumentReglementaireActions } from '@/modules/DocumentReglementaire/store/action-types';
import { SortType, sortValue } from '@/models/SortType';
import { store } from '@/plugins/store';
import {
    DocumentReglementaireToDownload,
    FilterDocumentsReglementaire
} from '@/modules/DocumentReglementaire/services/DocumentReglementaireService';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';
import { DocumentReglementaire } from '@/models/DocumentReglementaire';
import { TableHeader } from '@/models/TableHeader';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import ModalDownloadDocuments from '@/components/ui-components/ModalDownloadDocuments.vue';
import ModalChoixNomZip from '@/components/ui-components/ModalChoixNomZip.vue';
import ModalChoixTypeDocuments from '@/modules/DocumentReglementaire/pages/ModalChoixTypeDocuments.vue';

interface Data {
    documentReglementaireToDownload: DocumentReglementaireToDownload;
    downloadAllFilter: FilterDocumentsReglementaire;
    isDownloadAll: boolean;
    documentTypeList: TypeDocumentReglementaire[];
    documentListToDownload: string[];
    displayModalTypeDocument: boolean;
    filter: {
        documentTypeList: TypeDocumentReglementaire[];
    };
    showModal: boolean;
    showModalNomZip: boolean;
}

export default defineComponent({
    name: 'DocumentReglementaireTab',
    components: {
        ModalChoixTypeDocuments,
        ModalChoixNomZip,
        ModalDownloadDocuments,
        DataTable,
        LoaderComponent,
        BorderedIconButton,
        ButtonBlue,
        WaitingModal,
    },
    props: {
        phaseId: {
            type: Number,
            required: true,
        },
    },
    data(): Data {
        return {
            showModal: false,
            showModalNomZip: false,
            documentReglementaireToDownload: {},
            downloadAllFilter: {},
            isDownloadAll: false,
            documentTypeList: [],
            displayModalTypeDocument: true,
            filter: {
                documentTypeList: [],
            },
            documentListToDownload: [],
        };
    },
    setup() {
        const content = ref<HTMLElement | null>(null);
        return {
            content,
        };
    },
    created() {
        this.getTypesDocuments();
    },
    beforeMount() {
        this.documentTypeList = this.typeDocumentList;
        this.filter.documentTypeList = this.filterDocumentType.filter(
            type => type.source === DocumentTypeSource.DATABASE
        );

        if (!this.displayModalTypeDocument) {
            this.getDocumentReglementaireData();
        }
        if (this.filter.documentTypeList.length > 0) {
            this.displayModalTypeDocument = false;
        }
    },
    computed: {
        documentReglementaireState() {
            return store.state.equipementState.documentReglementaire;
        },
        tableData(): DocumentReglementaire[] {
            return this.$store.getters['equipementState/getDocumentReglementaireItems'].map(
                ({
                    uniqueDocumentId,
                    interventionId,
                    indiceFiche,
                    typeDocumentReglementaireDTO,
                    dateIntervention,
                    nomDocument,
                }: DocumentReglementaire) => {
                    return {
                        uniqueDocumentId: uniqueDocumentId,
                        interventionId:
                            typeDocumentReglementaireDTO.id !== null && typeDocumentReglementaireDTO.id > 9
                                ? interventionId + '-' + nomDocument
                                : indiceFiche
                                ? `${interventionId}-${indiceFiche}`
                                : interventionId,
                        typesDocumentReglementaire: typeDocumentReglementaireDTO.libelle,
                        dateIntervention: dateIntervention ? new Date(dateIntervention).toLocaleDateString() : '',
                    };
                }
            );
        },
        typeDocumentList(): TypeDocumentReglementaire[] {
            return this.$store.getters['documentReglementaireState/getTypesDocumentsReglementaires'].filter(
                (type: TypeDocumentReglementaire) => type.source === DocumentTypeSource.DATABASE
            );
        },
        tableHeader(): TableHeader[] {
            return this.$store.getters['equipementState/getDocumentReglementaireTableHeader'];
        },
        documentList(): DocumentReglementaire[] {
            return this.$store.getters['equipementState/getDocumentReglementaireItems'];
        },
        isLoading() {
            return this.$store.getters['equipementState/getDocumentReglementaireIsLoading'];
        },
        page() {
            return this.$store.getters['equipementState/getDocumentReglementairePage'];
        },
        numberOfPages() {
            return this.$store.getters['equipementState/getDocumentReglementaireNbOfPages'];
        },
        scroll() {
            return this.$store.getters['equipementState/getDocumentReglementaireScroll'];
        },
        filterDocumentType(): any[] {
            return this.$store.getters['equipementState/getDocumentReglementaireFilter'].filterDocumentType;
        },
        isDownloadDisabled(): boolean {
            return this.tableData.length === 0 || this.documentListToDownload.length === 0;
        },
        count() {
            return this.$store.getters['equipementState/getDocumentReglementaireCount'];
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['equipementState/getDocumentReglementaireSort'];
        },
        iconeDownload() {
            return require('@/assets/img/pdf-icon.svg');
        },
        closeIcon() {
            return require('@/assets/img/close-white.svg');
        },
        isZipLoading(): boolean {
            return this.$store.getters['equipementState/getZipIsLoading'];
        },
        isListTypesLoading(): boolean {
            return this.$store.getters['equipementState/getListTypesIsLoading'];
        },
        nbDocumentsModal(): number {
            return this.$store.getters['equipementState/getNombreDocumentModal'];
        },
    },
    methods: {
        getTypesDocuments() {
            this.$store.dispatch(
                `documentReglementaireState/${DocumentReglementaireActions.GET_TYPES_DOCUMENTS_REGLEMENTAIRES}`,
                { isBim: true }
            );
        },
        getDocumentReglementaireData(page = 0) {
            const storeFilter = store.getters['equipementState/getDocumentReglementaireFilter'];
            const filters: FilterDocumentsReglementaire = {
                page,
                sort: this.documentReglementaireState.sort
                    ? sortValue(
                          this.documentReglementaireState.sort.sortData,
                          this.documentReglementaireState.sort.sortStatus
                      )
                    : undefined,
                typeDocumentsReglementaireIds:
                    storeFilter.filterDocumentType?.length === 0
                        ? []
                        : storeFilter.filterDocumentType
                              .map((type: TypeDocumentReglementaire) => type.id)
                              .filter((id: number | null): id is number => id !== null),
                typeDocumentsReglementaireCodes: storeFilter.filterDocumentType.map(
                    (type: TypeDocumentReglementaire) => type.code
                ),
                isBim: true,
                phaseId: [this.phaseId],
            };

            this.$store.dispatch(
                `documentReglementaireState/${DocumentReglementaireActions.GET_DOCUMENT_REGLEMENTAIRE}`,
                filters,
                { root: true }
            );
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_SORT}`, {
                sortData,
                sortStatus,
            });
            this.$store.commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_RESET_ITEMS}`);
            this.getDocumentReglementaireData();
        },
        doSelect(item: { uniqueDocumentId: string }) {
            if (this.documentListToDownload?.find(element => element === item.uniqueDocumentId)) {
                this.documentListToDownload = this.documentListToDownload.filter(
                    element => element !== item.uniqueDocumentId
                );
            } else {
                this.documentListToDownload?.push(item.uniqueDocumentId);
            }
        },
        modaleChoixTypesCancel() {
            this.displayModalTypeDocument = false;
        },
        downloadDocuments() {
            const documentToDownload: DocumentReglementaire[] = this.documentList.filter(document =>
                this.documentListToDownload.find(element => document.uniqueDocumentId === element)
            );
            this.documentReglementaireToDownload = {
                isBim: true,
                uniqueDocumentIds: documentToDownload.map(doc => doc.uniqueDocumentId),
                chantierIds: documentToDownload.map(doc => doc.chantierId),
                nomDocument:
                    documentToDownload[0].typeDocumentReglementaireDTO.libelle +
                    '_' +
                    documentToDownload[0].documentId +
                    '_' +
                    documentToDownload[0].indiceFiche,
            };
            if (documentToDownload.length === 1) {
                this.documentReglementaireToDownload.extension = this.$store.getters[
                    'equipementState/getDocumentReglementaireItems'
                ].filter(
                    (doc: DocumentReglementaire) =>
                        this.documentReglementaireToDownload.uniqueDocumentIds !== undefined &&
                        doc.uniqueDocumentId === this.documentReglementaireToDownload.uniqueDocumentIds[0]
                )[0].extension;

                this.$store.dispatch(
                    `documentReglementaireState/${DocumentReglementaireActions.GET_DOCUMENT_REGLEMENTAIRE_PDF}`,
                    this.documentReglementaireToDownload
                );
                this.documentListToDownload = [];
            } else {
                this.showModalNomZip = true;
            }
            this.isDownloadAll = false;
        },
        async downloadAllDocuments(nomZip: string) {
            if (this.isDownloadAll) {
                this.downloadAllFilter.isBim = true;
                this.downloadAllFilter.nomZip = nomZip;
                await this.$store.dispatch(
                    `documentReglementaireState/${DocumentReglementaireActions.GET_ALL_DOCUMENT_REGLEMENTAIRE_PDF}`,
                    this.downloadAllFilter
                );
            } else {
                this.documentReglementaireToDownload.isBim = true;
                this.documentReglementaireToDownload.nomZip = nomZip;
                await this.$store.dispatch(
                    `documentReglementaireState/${DocumentReglementaireActions.GET_DOCUMENT_REGLEMENTAIRE_PDF}`,
                    this.documentReglementaireToDownload
                );
                this.documentListToDownload = [];
            }
            this.showModalNomZip = false;
        },
        setFilterForDownloadAllDocuments(filterDoc: { documentTypeList: TypeDocumentReglementaire[] }): void {
            this.$store.commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`, true);
            this.downloadAllFilter = {
                typeDocumentsReglementaireIds: filterDoc.documentTypeList
                    .map(typeDocument => typeDocument.id)
                    .filter((id): id is number => id !== null),
                isForModal: true,
                size: 1,
            };
            this.$store
                .dispatch(
                    `documentReglementaireState/${DocumentReglementaireActions.GET_DOCUMENT_REGLEMENTAIRE}`,
                    this.downloadAllFilter
                )
                .then(() => {
                    this.$store.commit(
                        `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_ZIP_IS_LOADING}`,
                        false
                    );
                    if (this.nbDocumentsModal < 100) {
                        this.showModal = false;
                        this.showModalNomZip = true;
                    }
                });
        },

        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.$store.commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_PAGE}`, this.page + 1);
                this.getDocumentReglementaireData(this.page);
            }
        },
        validerTypes(types: TypeDocumentReglementaire[]): void {
            this.displayModalTypeDocument = false;
            this.filter.documentTypeList = types;
            this.$store.commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_RESET_ITEMS}`);
            this.$store.commit(
                `equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_FILTER_DOCUMENT_TYPE}`,
                this.filter.documentTypeList.length === this.documentTypeList.length ? [] : this.filter.documentTypeList
            );
            this.sort = { sortStatus: SortType.DESC, sortData: 'dateIntervention' };
            this.$store.commit(`equipementState/${EQUIPEMENT.DOCUMENT_REGLEMENTAIRE_SET_SORT}`, {
                sortStatus: SortType.DESC,
                sortData: 'dateIntervention',
            });
            this.getDocumentReglementaireData();
        },

        selectionnerTypeDocument(): void {
            this.displayModalTypeDocument = true;
            this.documentListToDownload = [];
        },
        cancel() {
            this.showModal = false;
        },
        downloadAll() {
            this.isDownloadAll = true;
            if (this.count < 100) {
                this.downloadAllFilter = {
                    typeDocumentsReglementaireIds: this.filter.documentTypeList
                        .map(typeDocument => typeDocument.id)
                        .filter((id): id is number => id !== null),
                };
                this.showModalNomZip = true;
            } else {
                this.showModal = true;
            }
        },
    },
});
</script>

<style scoped>
.dataTable > div:first-child {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.filter-container {
    display: flex;
    padding: 8px 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 8px;
}

.button-confirm {
    right: 16px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 20px;
}

.button-left {
    float: left;
    margin-right: 10px;
    height: 28px;
}
.button-right {
    float: right;
    height: 28px;
}
.container-header {
    margin-bottom: 8px;
}
</style>
